
import { useEffect, useState } from "react";
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useUserRoles } from "@/hooks/useUserRoles";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarEvent } from "@/pages/scheduling/types";
import TotalWorkCalendar from "@/pages/scheduling/components/TotalWorkCalendar";
import { addDays, endOfWeek, format, startOfWeek } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";

interface ClientSchedulesProps {
  clientId: string;
}

interface GuardContact {
  id: string;
  name: string;
  mobile_phone?: string;
  email?: string;
}

export const ClientSchedules = ({ clientId }: ClientSchedulesProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [clientTeams, setClientTeams] = useState<string[]>([]);
  const [allShifts, setAllShifts] = useState<CalendarEvent[]>([]);
  const [guardNames, setGuardNames] = useState<Record<string, string>>({});
  const [guardContacts, setGuardContacts] = useState<Record<string, GuardContact>>({});
  const [currentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<"calendar" | "list">("calendar");
  const { toast } = useToast();
  
  const dateRange = {
    start: startOfWeek(currentDate, { weekStartsOn: 1 }),
    end: endOfWeek(currentDate, { weekStartsOn: 1 })
  };

  useEffect(() => {
    const fetchClientTeams = async () => {
      try {
        // Get teams the client is assigned to
        const { data: teams, error: teamsError } = await supabase
          .from('client_teams')
          .select('team_id')
          .eq('client_id', clientId);

        if (teamsError) throw teamsError;

        if (!teams || teams.length === 0) {
          setIsLoading(false);
          return;
        }

        const teamIds = teams.map(t => t.team_id);
        setClientTeams(teamIds);
        
        // Fetch schedules for all these teams
        await fetchTeamSchedules(teamIds);
      } catch (error: any) {
        console.error('Error fetching client teams:', error);
        toast({
          title: "Error loading schedules",
          description: error.message || "Failed to load team schedules",
          variant: "destructive"
        });
        setIsLoading(false);
      }
    };

    if (clientId) {
      fetchClientTeams();
    }
  }, [clientId, toast]);

  const fetchTeamSchedules = async (teamIds: string[]) => {
    try {
      setIsLoading(true);
      
      // Get all guard profiles first to have their names
      const { data: guards, error: guardsError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, full_name, mobile_phone, email')
        .eq('Role', 'guard');
        
      if (guardsError) throw guardsError;
      
      // Create a map of guard IDs to their names
      const guardsMap: Record<string, string> = {};
      const contactsMap: Record<string, GuardContact> = {};
      
      guards?.forEach(guard => {
        guardsMap[guard.id] = guard.full_name || `${guard.first_name || ''} ${guard.last_name || ''}`.trim() || 'Unknown';
        contactsMap[guard.id] = {
          id: guard.id,
          name: guardsMap[guard.id],
          mobile_phone: guard.mobile_phone,
          email: guard.email
        };
      });
      
      setGuardNames(guardsMap);
      setGuardContacts(contactsMap);
      
      // Fetch schedules from all teams
      const allTeamShifts: CalendarEvent[] = [];
      
      for (const teamId of teamIds) {
        const { data: schedules, error: schedulesError } = await supabase
          .from('team_schedules')
          .select('*')
          .eq('team_id', teamId);
          
        if (schedulesError) throw schedulesError;
        
        if (schedules && schedules.length > 0) {
          // Format each schedule entry as a CalendarEvent
          const formattedShifts = schedules.map(schedule => {
            let guardNames: string[] = [];
            if (schedule.assigned_guards && Array.isArray(schedule.assigned_guards)) {
              guardNames = schedule.assigned_guards.map(id => guardsMap[id] || 'Unknown');
            }
            
            return {
              ...schedule,
              color: 'primary',
              guard_names: guardNames
            };
          });
          
          allTeamShifts.push(...formattedShifts);
        }
      }
      
      setAllShifts(allTeamShifts);
    } catch (error: any) {
      console.error('Error fetching team schedules:', error);
      toast({
        title: "Error loading schedules",
        description: error.message || "Failed to load team schedules",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDayClick = (date: Date) => {
    console.log('Day clicked:', date);
    // Can be implemented if needed
  };

  const handleEventClick = (event: CalendarEvent) => {
    console.log('Event clicked:', event);
    // Call the first guard in the event
    if (event.assigned_guards && event.assigned_guards.length > 0) {
      const guardId = event.assigned_guards[0];
      callGuard(guardId);
    } else {
      toast({
        description: "No guards assigned to this shift",
        variant: "destructive"
      });
    }
  };

  const callGuard = (guardId: string) => {
    const guard = guardContacts[guardId];
    if (!guard || !guard.mobile_phone) {
      toast({
        title: "Cannot call guard",
        description: guard ? `No phone number available for ${guard.name}` : "Guard not found",
        variant: "destructive"
      });
      return;
    }
    
    console.log(`Calling guard: ${guard.name} at ${guard.mobile_phone}`);
    window.location.href = `tel:${guard.mobile_phone}`;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-full max-w-sm" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (clientTeams.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        No teams assigned to this client yet.
      </div>
    );
  }

  if (allShifts.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        No shifts scheduled for the teams assigned to this client.
      </div>
    );
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <span>Security Schedule</span>
          <Tabs value={currentView} onValueChange={(v) => setCurrentView(v as "calendar" | "list")}>
            <TabsList>
              <TabsTrigger value="calendar">Calendar</TabsTrigger>
              <TabsTrigger value="list">List</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <TabsContent value="calendar" className="mt-0">
          <TotalWorkCalendar
            events={allShifts}
            currentDate={currentDate}
            dateRange={dateRange}
            guardId="all"
            handleDayClick={handleDayClick}
            handleEventClick={handleEventClick}
          />
        </TabsContent>
        
        <TabsContent value="list" className="mt-0">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Guards</TableHead>
                  <TableHead className="w-[80px]">Call</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allShifts
                  .sort((a, b) => fromLocalISOString(a.start_date).getTime() - fromLocalISOString(b.start_date).getTime())
                  .map((shift) => (
                    <TableRow key={shift.id}>
                      <TableCell className="font-medium">
                        {format(fromLocalISOString(shift.start_date), 'dd/MM/yyyy')}
                      </TableCell>
                      <TableCell>
                        {format(fromLocalISOString(shift.start_date), 'HH:mm')} - {format(fromLocalISOString(shift.end_date), 'HH:mm')}
                      </TableCell>
                      <TableCell>{shift.title}</TableCell>
                      <TableCell>
                        {shift.guard_names && shift.guard_names.length > 0 
                          ? shift.guard_names.join(', ') 
                          : 'No guards assigned'}
                      </TableCell>
                      <TableCell>
                        {shift.assigned_guards && shift.assigned_guards.length > 0 ? (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => shift.assigned_guards && callGuard(shift.assigned_guards[0])}
                            className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            <Phone className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            disabled
                            className="h-8 w-8 text-muted-foreground"
                          >
                            <Phone className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </CardContent>
    </Card>
  );
};
